import { Link, useLocation } from "react-router-dom";
import { Home, Search, ShoppingBag, MessageCircle, User } from "lucide-react";
import { motion } from "framer-motion";
import useUnreadChats from "../../hooks/useUnreadChats";
import { ChatBadge } from "../ChatBadge";

const TABS = [
  { to: "/cliente/home", label: "Início", icon: Home, testId: "tab-home" },
  { to: "/cliente/busca", label: "Buscar", icon: Search, testId: "tab-search" },
  { to: "/cliente/pedidos", label: "Pedidos", icon: ShoppingBag, testId: "tab-orders" },
  { to: "/cliente/chat", label: "Chat", icon: MessageCircle, testId: "tab-chats", chatBadge: true },
  { to: "/cliente/perfil", label: "Perfil", icon: User, testId: "tab-profile" },
];

// Routes that render their own bottom CTA — hide the global nav there.
const HIDE_NAV_PREFIXES = [
  "/cliente/carrinho",
  "/cliente/checkout",
  "/cliente/restaurante/",
  "/cliente/tracking/",
];

/**
 * Premium glassmorphism bottom navigation (mobile-first).
 * Mounted as fixed bottom strip with backdrop blur and active pill animation.
 */
export default function GlassBottomNav() {
  const location = useLocation();
  const { total: unreadTotal } = useUnreadChats();
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  if (HIDE_NAV_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-white/85 backdrop-blur-xl border-t border-black/5 pb-safe-bottom shadow-[0_-4px_24px_rgba(0,0,0,0.04)]"
      data-testid="cliente-bottom-nav"
    >
      <div className="grid grid-cols-5 max-w-3xl mx-auto h-20">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className="relative flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
              data-testid={tab.testId}
            >
              {active && (
                <motion.div
                  layoutId="bottom-nav-pill"
                  className="absolute inset-x-3 top-1.5 bottom-1.5 bg-[#FF6B00]/10 rounded-2xl -z-10"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <div className="relative">
                <Icon className={`w-7 h-7 transition-colors ${active ? "text-[#FF6B00]" : "text-gray-400"}`} strokeWidth={active ? 2.7 : 2.2} />
                {tab.chatBadge && unreadTotal > 0 && (
                  <ChatBadge count={unreadTotal} className="absolute -top-1.5 -right-2.5" testId="cliente-chat-badge" />
                )}
              </div>
              <span className={`text-[11px] font-bold transition-colors ${active ? "text-[#FF6B00]" : "text-gray-500"}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
